import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { createHead } from "@/components/createHead";
import { useToast } from "@/components/hooks/use-toast";
import { Page } from "@/components/Page";
import { PrivacyPolicy } from "@/components/PrivacyPolicy";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { deleteAccountData } from "@/lib/api";

export const Route = createFileRoute(
  "/_marketing-header-layout/privacy-policy",
)({
  component: RouteComponent,
  head: ({ match: { pathname } }) =>
    createHead({
      title: "Privacy Policy | Graze",
      ogTitle: "Privacy Policy",
      description: "Privacy Policy for Graze.social",
      pathname,
    }),
});

function RequestDataRemovalDialog() {
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const onDeleteDialogOpenChange = (open: boolean) => {
    if (deleteAccountDataMutation.isPending) return;
    setDeleteDialogOpen(open);
  };
  const deleteAccountDataMutation = useMutation({
    mutationFn: async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formData = new FormData(e.target as HTMLFormElement);
      const email = formData.get("email") as string;
      await deleteAccountData({ email });
    },
    onSuccess: () => {
      toast({
        title: "Data deletion request sent",
        description:
          "You will receive an email when your data has been deleted.",
      });
      setDeleteDialogOpen(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  return (
    <AlertDialog
      open={deleteDialogOpen}
      onOpenChange={onDeleteDialogOpenChange}
    >
      <AlertDialogTrigger asChild>
        <Button variant="destructive" size="lg">
          Request Data Removal
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <form onSubmit={deleteAccountDataMutation.mutate}>
          <AlertDialogHeader>
            <AlertDialogTitle>Request Data Removal</AlertDialogTitle>
            <AlertDialogDescription>
              Request to remove your data from our systems.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-4 py-4 ">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Label htmlFor="email">Email</Label>
              </div>
              <Input id="email" type="email" name="email" required />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction type="submit" variant="destructive">
              Request Data Removal
            </AlertDialogAction>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function RouteComponent() {
  return (
    <Page className="md:max-w-240">
      <h1 className="text-4xl md:text-6xl font-bold instrument-serif">
        Privacy Policy
      </h1>
      <PrivacyPolicy />
      <RequestDataRemovalDialog />
    </Page>
  );
}
